import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export type OwnerScope = 'CLIENT' | 'PARTNER';

export interface GoLiveCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string;
  fixHint: string;
}

export interface OwnerRow {
  scope: OwnerScope;
  ownerId: number;
  name: string | null;
  code: string | null;
  mode: 'LIVE' | 'TEST';
  isActive: boolean;
}

export interface GoLiveStatus {
  scope: OwnerScope;
  ownerId: number;
  name: string | null;
  code: string | null;
  mode: 'LIVE' | 'TEST';
  canGoLive: boolean;
  checks: GoLiveCheck[];
}

/**
 * Per-client / per-partner live readiness. Mode belongs to the owner, so one client can be
 * taking real money while another is still piloting. Going LIVE is gated on every check
 * passing; going back to TEST never is.
 */
@Injectable({ providedIn: 'root' })
export class GoLiveService {
  private http = inject(HttpClient);
  private readonly api = `${environment.apiUrl}/GoLive`;

  owners() {
    return this.http.get<any>(`${this.api}/Owners`);
  }

  status(scope: OwnerScope, ownerId: number) {
    return this.http.get<any>(`${this.api}/${scope === 'CLIENT' ? 'Client' : 'Partner'}/${ownerId}`);
  }

  setMode(scope: OwnerScope, ownerId: number, mode: 'LIVE' | 'TEST') {
    return this.http.put<any>(`${this.api}/${scope === 'CLIENT' ? 'Client' : 'Partner'}/${ownerId}`, { mode });
  }

  // ---- partner gateway assignment (the partner-side Payment Configuration) ----
  partnerGateways() {
    return this.http.get<any>(`${environment.apiUrl}/PartnerPaymentConfig/Partners`);
  }

  gatewayOptions(partnerId: number) {
    return this.http.get<any>(`${environment.apiUrl}/PartnerPaymentConfig/Options`, { params: { partnerId } });
  }

  /** Pass null to clear, so the partner falls back to the global gateway for its mode. */
  assignGateway(partnerId: number, paymentIntegrationId: number | null) {
    return this.http.post<any>(`${environment.apiUrl}/PartnerPaymentConfig/Save`, { partnerId, paymentIntegrationId });
  }
}

/** A partner row on the gateway-assignment screen. */
export interface PartnerGatewayRow {
  partnerId: number;
  partnerName: string | null;
  partnerCode: string | null;
  mode: 'LIVE' | 'TEST';
  integrationId: number | null;
  integrationLabel: string | null;
  integrationIsLive: boolean | null;
  /** What a payment would ACTUALLY use right now — config, SPECIFIC or GLOBAL. */
  effectiveIntegrationId: number | null;
  effectiveLabel: string | null;
  effectiveSource: string | null;
}
